import { createSlice } from '@reduxjs/toolkit';
import sum from 'lodash/sum';
import uniqBy from 'lodash/uniqBy';
// utils
import axios from '../../utils/axios';
//
import { dispatch } from '../store';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  products: [],
  productNames: [],
  total: 0,
  product: null,
  sortBy: null,
  range: [0, 999999],
  filters: {
    gender: [],
    category: 'All',
    colors: [],
    priceRange: '',
    rating: '',
  },
  checkout: {
    activeStep: 0,
    cart: [],
    subtotal: 0,
    total: 0,
    discount: 0,
    shipping: 0,
    billing: null,
  },
};

const slice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET PRODUCTS
    getProductsSuccess(state, action) {
      state.isLoading = false;
      state.total = action.payload.total;
      state.products = action.payload.results || [];
    },

    // GET PRODUCT
    getProductSuccess(state, action) {
      state.isLoading = false;
      state.product = action.payload;
    },

    //  SORT & FILTER PRODUCTS
    sortByProducts(state, action) {
      state.sortBy = action.payload;
    },

    filterProducts(state, action) {
      state.filters.gender = action.payload.gender;
      state.filters.category = action.payload.category;
      state.filters.colors = action.payload.colors;
      state.filters.priceRange = action.payload.priceRange;
      state.filters.rating = action.payload.rating;
    },

    // CHECKOUT
    getCart(state, action) {
      const cart = action.payload;

      const subtotal = sum(cart.map((cartItem) => cartItem.price * cartItem.quantity));
      const discount = cart.length === 0 ? 0 : state.checkout.discount;
      const shipping = cart.length === 0 ? 0 : state.checkout.shipping;
      const billing = cart.length === 0 ? null : state.checkout.billing;

      state.checkout.cart = cart;
      state.checkout.discount = discount;
      state.checkout.shipping = shipping;
      state.checkout.billing = billing;
      state.checkout.subtotal = subtotal;
      state.checkout.total = subtotal - discount;
    },

    addCart(state, action) {
      const product = action.payload;
      const isEmptyCart = state.checkout.cart.length === 0;

      if (isEmptyCart) {
        state.checkout.cart = [...state.checkout.cart, product];
      } else {
        state.checkout.cart = state.checkout.cart.map((_product) => {
          const isExisted = _product.id === product.id;
          if (isExisted) {
            return {
              ..._product,
              quantity: _product.quantity + 1,
            };
          }
          return _product;
        });
      }
      state.checkout.cart = uniqBy([...state.checkout.cart, product], 'id');
    },

    deleteCart(state, action) {
      const updateCart = state.checkout.cart.filter((item) => item.id !== action.payload);

      state.checkout.cart = updateCart;
    },

    resetCart(state) {
      state.checkout.activeStep = 0;
      state.checkout.cart = [];
      state.checkout.total = 0;
      state.checkout.subtotal = 0;
      state.checkout.discount = 0;
      state.checkout.shipping = 0;
      state.checkout.billing = null;
    },

    onBackStep(state) {
      state.checkout.activeStep -= 1;
    },

    onNextStep(state) {
      state.checkout.activeStep += 1;
    },

    onGotoStep(state, action) {
      const goToStep = action.payload;
      state.checkout.activeStep = goToStep;
    },

    increaseQuantity(state, action) {
      const productId = action.payload;
      const updateCart = state.checkout.cart.map((product) => {
        if (product.id === productId) {
          return {
            ...product,
            quantity: product.quantity + 1,
          };
        }
        return product;
      });

      state.checkout.cart = updateCart;
    },

    decreaseQuantity(state, action) {
      const productId = action.payload;
      const updateCart = state.checkout.cart.map((product) => {
        if (product.id === productId) {
          return {
            ...product,
            quantity: product.quantity - 1,
          };
        }
        return product;
      });

      state.checkout.cart = updateCart;
    },

    createBilling(state, action) {
      state.checkout.billing = action.payload;
    },

    applyDiscount(state, action) {
      const discount = action.payload;
      state.checkout.discount = discount;
      state.checkout.total = state.checkout.subtotal - discount;
    },

    applyShipping(state, action) {
      const shipping = action.payload;
      state.checkout.shipping = shipping;
      state.checkout.total = state.checkout.subtotal - state.checkout.discount + shipping;
    },

    duplicateProductSuccess(state, action) {
      state.isLoading = false;
      state.products = [...state.products, action.payload];
    },

    deleteProductSuccess(state, action) {
      state.isLoading = false;
      state.products = state.products.filter((product) => product._id !== action.payload);
    },

    deleteProductsSuccess(state, action) {
      state.isLoading = false;
      state.products = state.products.filter((product) => !action.payload.includes(product._id));
    },

    getProductNamesSuccess(state, action) {
      state.isLoading = false;
      state.productNames = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  getCart,
  startLoading,
  addCart,
  resetCart,
  onGotoStep,
  onBackStep,
  onNextStep,
  deleteCart,
  createBilling,
  applyShipping,
  applyDiscount,
  increaseQuantity,
  decreaseQuantity,
  sortByProducts,
  filterProducts,
  duplicateProductSuccess,
  deleteProductSuccess,
  deleteProductsSuccess,
  getProductNamesSuccess,
  getProductsSuccess,
} = slice.actions;

// ----------------------------------------------------------------------

export function getProducts(page, limit, query, sortBy, orderBy) {
  return async () => {
    try {
      const response = await axios.post('/product/pagination/' + page + '/' + limit, { query, sortBy, orderBy });
      dispatch(slice.actions.getProductsSuccess(response.data || []));
    } catch (error) {
      dispatch(slice.actions.getProductsSuccess({
        results: [],
        total: 0,
      }));
    }
  };
}

export function getCasks(page, limit, query, sortBy, orderBy) {
  return async () => {
    try {
      const response = await axios.post('/product/cask/pagination/' + page + '/' + limit, { query, sortBy, orderBy });
      dispatch(slice.actions.getProductsSuccess(response.data || []));
    } catch (error) {
      dispatch(slice.actions.getProductsSuccess({
        results: [],
        total: 0,
      }));
    }
  };
}

export function getProductNames(query) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      query.prod_type = 'Auction'
      const response = await axios.post('/product/name/all', { query });
      dispatch(slice.actions.getProductNamesSuccess(response.data || []));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
// ----------------------------------------------------------------------

export function getProduct(id) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/product/' + id);
      dispatch(slice.actions.getProductSuccess(response.data.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function duplicateProduct(type, id) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/product/duplicate/' + id, {
        type,
      });
      dispatch(slice.actions.duplicateProductSuccess(response.data.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  }
}

// ----------------------------------------------------------------------
export function deleteProduct(id) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.delete('/product/delete/' + id);
      dispatch(slice.actions.deleteProductSuccess(id));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------
export function deleteProducts(ids) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.post('/product/delete/', { ids });
      dispatch(slice.actions.deleteProductsSuccess(ids));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}